import styled, { css } from 'styled-components';

const resolutionTemplate = [
  '\n    @media (-webkit-min-device-pixel-ratio: ',
  '), (min-resolution: ',
  'dppx) {\n      ',
  '\n    }\n  '
];

/**
 * コンポーネントに共通 / PC / SPのスタイルを適用する
 * @param {Function} component => css[]
 */
export const applyStyles = component => (...styles) => styled(component)`
  ${styles};
`;

/**
 * バイナリ形式のファイルをダウンロードする
 * @param {String} fileName
 * @param {String} url
 */
export const downloadFile = (fileName, url) => {
  const anchor = document.createElement('a');
  if (isSP()) {
    const reader = new FileReader();
    reader.onloadend = () => {
      anchor.href = reader.result;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    };
    reader.readAsDataURL(url);
  } else {
    anchor.href = URL.createObjectURL(url);
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    URL.revokeObjectURL(url);
    anchor.remove();
  }
};

/**
 * 解像度に対応したメディアクエリを作成する
 * @param {*} dppx
 */
export const minResolution = dppx => {
  return function() {
    return css(
      resolutionTemplate,
      '' + dppx,
      '' + dppx,
      css.apply(undefined, arguments)
    );
  };
};

/**
 * 電話番号・郵便番号のハイフン・空白・タブを取り除く
 * @param {String} input
 */
export const replaceHyphenAndSpaces = input => {
  return input.replace(/[-ー 　		]/g, '');
};

/**
 * 全角の数値を半角に変換する
 * @param {String} input
 */
export const convertDoubleWidthNumber = input => {
  const mappings = [
    ['０', '0'],
    ['１', '1'],
    ['２', '2'],
    ['３', '3'],
    ['４', '4'],
    ['５', '5'],
    ['６', '6'],
    ['７', '7'],
    ['８', '8'],
    ['９', '9'],
    ['−', '-']
  ];

  return mappings.reduce(
    (temp, [src, replacer]) => temp.replace(new RegExp(src, 'g'), replacer),
    input
  );
};

/**
 * 全角の英字を半角に変換する
 * @param {String} input
 */
export const convertDoubleWidthAlpha = input =>
  input.replace(/[Ａ-Ｚａ-ｚ]/g, str =>
    String.fromCharCode(str.charCodeAt(0) - 0xfee0)
  );

/**
 * ひらがなをカタカナに変換する
 * @param {String} input
 */
export const convertKana = input => {
  return input
    .split('')
    .map(char =>
      /[ぁ-ん]/.test(char)
        ? String.fromCharCode(char.charCodeAt() + 0x60)
        : char
    )
    .join('');
};

/**
 * 半角スペースを全角スペースに変換する
 *
 * @param {String} input
 */
export const convertSpace = input => {
  return input.replace(/ /g, '　');
};

/**
 * 全角スペースを半角に変換する
 *
 * @param {String} input
 */
export const convertDoubleWidthSpace = input => {
  return input.replace(/[　		]/g, ' ');
};

/**
 * 特定のキーが押された場合のみ入力を許可する
 * @param {KeyboardEvent} event
 * @param {RegExp} allowedKeys
 */
export const allowExactKeysOnly = (event, allowedKeys) => {
  const systemKeys = [
    'Backspace',
    'Enter',
    'Delete',
    'ArrowUp',
    'ArrowRight',
    'ArrowDown',
    'ArrowLeft',
    'Tab'
  ];
  const { key } = event;

  if (!systemKeys.includes(key) && !allowedKeys.test(key)) {
    event.preventDefault();
  }
};

/**
 * Smartphone か判定する
 */
export const isSP = () => {
  return (
    navigator.userAgent.includes('iPad') ||
    navigator.userAgent.includes('iPhone') ||
    navigator.userAgent.includes('Android')
  );
};

/**
 * 全角カナを半角カナに変換する
 * @param {String} input
 */
export const convertFullWidthKana = input => {
  const mappings = [
    ['ア', 'ｱ'],
    ['イ', 'ｲ'],
    ['ウ', 'ｳ'],
    ['エ', 'ｴ'],
    ['オ', 'ｵ'],
    ['カ', 'ｶ'],
    ['キ', 'ｷ'],
    ['ク', 'ｸ'],
    ['ケ', 'ｹ'],
    ['コ', 'ｺ'],
    ['サ', 'ｻ'],
    ['シ', 'ｼ'],
    ['ス', 'ｽ'],
    ['セ', 'ｾ'],
    ['ソ', 'ｿ'],
    ['タ', 'ﾀ'],
    ['チ', 'ﾁ'],
    ['ツ', 'ﾂ'],
    ['テ', 'ﾃ'],
    ['ト', 'ﾄ'],
    ['ナ', 'ﾅ'],
    ['ニ', 'ﾆ'],
    ['ヌ', 'ﾇ'],
    ['ネ', 'ﾈ'],
    ['ノ', 'ﾉ'],
    ['ハ', 'ﾊ'],
    ['ヒ', 'ﾋ'],
    ['フ', 'ﾌ'],
    ['ヘ', 'ﾍ'],
    ['ホ', 'ﾎ'],
    ['マ', 'ﾏ'],
    ['ミ', 'ﾐ'],
    ['ム', 'ﾑ'],
    ['メ', 'ﾒ'],
    ['モ', 'ﾓ'],
    ['ヤ', 'ﾔ'],
    ['ユ', 'ﾕ'],
    ['ヨ', 'ﾖ'],
    ['ラ', 'ﾗ'],
    ['リ', 'ﾘ'],
    ['ル', 'ﾙ'],
    ['レ', 'ﾚ'],
    ['ロ', 'ﾛ'],
    ['ワ', 'ﾜ'],
    ['ヲ', 'ｦ'],
    ['ン', 'ﾝ'],
    ['ァ', 'ｧ'],
    ['ィ', 'ｨ'],
    ['ゥ', 'ｩ'],
    ['ェ', 'ｪ'],
    ['ォ', 'ｫ'],
    ['ッ', 'ｯ'],
    ['ャ', 'ｬ'],
    ['ュ', 'ｭ'],
    ['ョ', 'ｮ'],
    ['ガ', 'ｶﾞ'],
    ['ギ', 'ｷﾞ'],
    ['グ', 'ｸﾞ'],
    ['ゲ', 'ｹﾞ'],
    ['ゴ', 'ｺﾞ'],
    ['ザ', 'ｻﾞ'],
    ['ジ', 'ｼﾞ'],
    ['ズ', 'ｽﾞ'],
    ['ゼ', 'ｾﾞ'],
    ['ゾ', 'ｿﾞ'],
    ['ダ', 'ﾀﾞ'],
    ['ヂ', 'ﾁﾞ'],
    ['ヅ', 'ﾂﾞ'],
    ['デ', 'ﾃﾞ'],
    ['ド', 'ﾄﾞ'],
    ['バ', 'ﾊﾞ'],
    ['ビ', 'ﾋﾞ'],
    ['ブ', 'ﾌﾞ'],
    ['ベ', 'ﾍﾞ'],
    ['ボ', 'ﾎﾞ'],
    ['パ', 'ﾊﾟ'],
    ['ピ', 'ﾋﾟ'],
    ['プ', 'ﾌﾟ'],
    ['ペ', 'ﾍﾟ'],
    ['ポ', 'ﾎﾟ'],
    ['ヴ', 'ｳﾞ'],
    ['ヷ', 'ﾜﾞ'],
    ['ヺ', 'ｦﾞ'],
    ['ー', 'ｰ'],
    ['・', '･'],
    ['゛', 'ﾞ'],
    ['゜', 'ﾟ']
  ];

  return mappings.reduce(
    (temp, [src, replacer]) => temp.replace(new RegExp(src, 'g'), replacer),
    input
  );
};

/**
 * 全角の記号を半角に変換する
 * @param {String} input
 */
export const convertDoubleWidthSymbol = input => {
  const mappings = [
    ['！', '!'],
    ['＃', '#'],
    ['＄', '$'],
    ['％', '%'],
    ['＆', '&'],
    ['’', "'"],
    ['＊', '*'],
    ['＋', '+'],
    ['‐', '-'],
    ['／', '/'],
    ['＝', '='],
    ['ー', '-'],
    ['？', '?'],
    ['＾', '^'],
    ['＿', '_'],
    ['｀', '`'],
    ['｛', '{'],
    ['｜', '|'],
    ['｝', '}'],
    ['〜', '~'],
    ['．', '.'],
    ['＠', '@']
  ];

  return mappings.reduce(
    (temp, [src, replacer]) => temp.replace(new RegExp(src, 'g'), replacer),
    input
  );
};



// WEBPACK FOOTER //
// ./src/util/helpers.js